import React from 'react';
import { AllTimeStatsResponse } from '../../../shared/types';
import './AllTimeStats.css';
import { getOwnerColor } from '../utils/ownerColors';
import { getFantasyTeamName } from '../utils/teamNames';

interface AllTimeStatsProps {
  allTimeStats: AllTimeStatsResponse;
}

const AllTimeStats: React.FC<AllTimeStatsProps> = ({ allTimeStats }) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Sort members by total championships, then by average score
  const sortedStats = [...allTimeStats.stats].sort((a, b) => {
    if (a.totalChampionships !== b.totalChampionships) {
      return b.totalChampionships - a.totalChampionships;
    }
    return b.averageScore - a.averageScore;
  });

  if (sortedStats.length === 0) {
    return (
      <div className="all-time-stats">
        <div className="no-stats">
          <h3>No All-Time Statistics Available</h3>
          <p>Complete multiple seasons to see all-time statistics here!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="all-time-stats">
      <div className="stats-header">
        <h2>All-Time Statistics</h2>
        <p className="last-updated">Last updated: {formatDate(allTimeStats.lastUpdated)}</p>
      </div>

      <div className="stats-grid">
        {sortedStats.map((member, index) => (
          <div key={member.memberName} className={`member-card rank-${index + 1}`}>
            <div className="member-header">
              <h3 className="member-name" style={{ color: getOwnerColor(member.memberName) }}>
                {getFantasyTeamName(member.memberName)}
              </h3>
              {member.totalChampionships > 0 && (
                <div className="championship-badges">
                  {Array.from({ length: member.totalChampionships }).map((_, i) => (
                    <span key={i} className="trophy">🏆</span>
                  ))}
                </div>
              )}
            </div>

            <div className="stats-grid-member">
              <div className="stat-item primary">
                <span className="stat-label">Championships</span>
                <span className="stat-value">{member.totalChampionships}</span>
              </div>

              <div className="stat-item primary">
                <span className="stat-label">Total Seasons</span>
                <span className="stat-value">{member.totalSeasons}</span>
              </div>

              <div className="stat-item">
                <span className="stat-label">Total Wins</span>
                <span className="stat-value">{member.totalWins}</span>
              </div>

              <div className="stat-item">
                <span className="stat-label">Average Score</span>
                <span className="stat-value">{member.averageScore.toFixed(1)}</span>
              </div>

              <div className="stat-item highlight">
                <span className="stat-label">Best Season</span>
                <span className="stat-value">
                  {member.bestSeasonScore} ({member.bestSeasonYear})
                </span>
              </div>

              <div className="stat-item">
                <span className="stat-label">Worst Season</span>
                <span className="stat-value">
                  {member.worstSeasonScore} ({member.worstSeasonYear})
                </span>
              </div>
            </div>

            <div className="member-achievements">
              {member.totalChampionships > 0 && (
                <div className="achievement">
                  <span className="achievement-icon">👑</span>
                  <span className="achievement-text">
                    {member.totalChampionships === 1 ? 'Champion' : `${member.totalChampionships}x Champion`}
                  </span>
                </div>
              )}
              
              {member.averageScore >= 50 && (
                <div className="achievement">
                  <span className="achievement-icon">⭐</span>
                  <span className="achievement-text">High Performer</span>
                </div>
              )}
              
              {member.totalSeasons >= 3 && (
                <div className="achievement">
                  <span className="achievement-icon">🏈</span>
                  <span className="achievement-text">Veteran Player</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="league-summary">
        <h3>League Summary</h3>
        <div className="summary-stats">
          <div className="summary-item">
            <span className="summary-label">Total Seasons Played</span>
            <span className="summary-value">
              {Math.max(...sortedStats.map(s => s.totalSeasons))}
            </span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Total Games Won</span>
            <span className="summary-value">
              {sortedStats.reduce((sum, s) => sum + s.totalWins, 0)}
            </span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Most Championships</span>
            <span className="summary-value">
              {Math.max(...sortedStats.map(s => s.totalChampionships))}
            </span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Highest Single Season</span>
            <span className="summary-value">
              {Math.max(...sortedStats.map(s => s.bestSeasonScore))}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AllTimeStats;
